import { RootState } from "app/services/store";
import { useDispatch, useSelector } from "react-redux";
import { verifyHashedPassword } from "../utils/HashingUtil";
import {
  increaseLocalRetries,
  resetLocalRetries,
} from "app/store/slices/authorisation";
import { useMemo } from "react";

export function usePasswordVerification() {
  const isOffline = useSelector(
    (state: RootState) => !state.network?.networkState?.isInternetReachable,
  );

  const dispatch = useDispatch();

  const userEmail = useSelector(
    (state: RootState) => state.authorisation?.user?.email,
  );
  const hash = useSelector((state: RootState) => state.authorisation?.hash);
  const localRetries = useSelector(
    (state: RootState) => state.authorisation?.localRetries,
  );

  const retriesLeft = useMemo(
    () => (!localRetries ? undefined : 3 - localRetries),
    [localRetries],
  );
  const verifyPassword = async ({
    email,
    password,
  }: {
    email?: string;
    password: string;
  }) => {
    if (isOffline) {
      if (!hash) throw new Error("Unable to verify password");

      if (retriesLeft === 0)
        throw new Error(
          "Account is temporarily locked until network is back online",
        );

      // Should alwasy match - redundant (Handle login with a user instead)
      if (userEmail !== email)
        throw new Error("The current email does not match");

      const match = await verifyHashedPassword(password, hash);

      if (!match) {
        dispatch(increaseLocalRetries());
        throw new Error("Password does not match");
      }
    }
    dispatch(resetLocalRetries());
  };

  return { verifyPassword, retriesLeft };
}
